<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <div class="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div class="flex-1">
          <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">{{ $t('BUSINESS_DATA.ITEMS.TITLE') }}</h2>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">{{ $t('BUSINESS_DATA.ITEMS.DESCRIPTION') }}</p>
        </div>
        
        <!-- Mobile: Stack buttons vertically -->
        <div class="flex flex-col xl:flex-row xl:items-center gap-2 xl:gap-2">
          <!-- Mobile: Primary action first -->
          <woot-button
            size="small"
            class="!bg-grass-500 !text-white w-full xl:w-auto order-1 xl:order-3"
            @click="$emit('add-data-item')"
          >
            <lucide-icon name="plus" size="16" class="mr-2" />
            {{ $t('BUSINESS_DATA.ITEMS.ADD_NEW') }}
          </woot-button>
          
          <!-- Mobile: Secondary actions in a row -->
          <div class="flex gap-2 order-2 xl:order-1">
            <woot-button
              class="!bg-grass-500 !text-white flex-1 xl:flex-none"
              size="small"
              :loading="isExporting"
              :disabled="isExporting"
              @click="onExport"
            >
              <lucide-icon name="download" size="14" class="mr-1 lg:mr-2" />
              <span class="hidden xs:inline">{{ $t('GENERAL.DOWNLOAD') }}</span> XLSX
            </woot-button>
            <woot-button
              class="!bg-grass-500 !text-white flex-1 xl:flex-none"
              size="small"
              :loading="isImporting"
              :disabled="isImporting"
              @click="triggerImport"
            >
              <lucide-icon name="upload" size="14" class="mr-1 lg:mr-2" />
              <span class="hidden xs:inline">{{ $t('GENERAL.UPLOAD') }}</span> XLSX/CSV
            </woot-button>
          </div>
          
          <input
            ref="fileInput"
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            class="hidden"
            @change="onFileSelected"
          />
        </div>
      </div>
      <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {{ $t('BUSINESS_DATA.ITEMS.IMPORT_HINT') }}
      </p>
    </div>
    
    <!-- Search and Filters -->
    <div class="flex flex-col xl:flex-row gap-4">
      <!-- Search Input -->
      <div class="flex-1">
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="`🔍 ${$t('BUSINESS_DATA.ITEMS.SEARCH_PLACEHOLDER')}`"
            class="w-full pl-4 pr-10 py-3 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md placeholder:text-slate-400"
            @input="onSearch"
          />
          <div v-if="isSearching" class="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div class="w-4 h-4 border-2 border-slate-200 dark:border-slate-600 border-t-hichat-500 rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
      
      <!-- Filter Controls -->
      <div class="flex flex-col xl:flex-row gap-3">
        <!-- Category Filter -->
        <div class="relative">
          <select
            v-model="selectedCategory"
            class="w-full xl:w-48 pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="padding-left: 2.5rem; -webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">{{ $t('BUSINESS_DATA.ITEMS.ALL_CATEGORIES') }}</option>
            <option
              v-for="category in normalizedCategories"
              :key="category.value"
              :value="category.value"
            >
              {{ category.label }}
            </option>
          </select>
        </div>
        
        <!-- Status Filter -->
        <div class="relative">
          <fluent-icon icon="circle" size="16" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none z-10" />
          <select 
            v-model="selectedStatus" 
            class="w-full xl:w-40 pl-10 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">{{ $t('BUSINESS_DATA.ITEMS.ALL_STATUS') }}</option>
            <option value="true">{{ $t('BUSINESS_DATA.ITEMS.ACTIVE') }}</option>
            <option value="false">{{ $t('BUSINESS_DATA.ITEMS.INACTIVE') }}</option>
          </select>
        </div>
        
        <!-- Source Filter -->
        <div class="relative">
          <select 
            v-model="selectedSource" 
            class="w-full xl:w-48 pl-4 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="">{{ $t('BUSINESS_DATA.ITEMS.ALL_SOURCES') }}</option>
            <option value="manual">{{ $t('BUSINESS_DATA.ITEMS.MANUAL_ITEMS') }}</option>
            <option 
              v-for="source in webSources" 
              :key="source.id" 
              :value="source.id"
            >
              {{ source.name }}
            </option>
          </select>
        </div>

        <!-- Sort by created date -->
        <div class="relative">
          <select
            v-model="selectedSort"
            class="w-full xl:w-44 pl-4 pr-10 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-hichat-500/20 focus:border-hichat-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
            style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
            @change="onFilterChange"
          >
            <option value="created_at_desc">{{ $t('BUSINESS_DATA.ITEMS.SORT.NEWEST') }}</option>
            <option value="created_at_asc">{{ $t('BUSINESS_DATA.ITEMS.SORT.OLDEST') }}</option>
          </select>
        </div>
      </div>
    </div>

    <BulkActionsBar
      :selected-count="selectedCount"
      :bulk-action="bulkAction"
      :is-bulk-deleting="isBulkDeleting"
      :is-all-selected-on-page="isAllSelectedOnPage"
      @activate="emitBulkStatus(true)"
      @deactivate="emitBulkStatus(false)"
      @delete="$emit('bulk-delete')"
      @clear-selection="$emit('clear-selection')"
      @toggle-select-page="toggleSelectOnPage"
    />
    
    <!-- Content Area -->
    <div>
      <!-- Loading State -->
      <div v-if="isLoading" class="py-8 flex items-center justify-center">
        <Spinner size="md" color-scheme="primary" />
      </div>
      
      <!-- Empty State -->
      <div v-else-if="dataItems.length === 0" class="text-center py-12">
        <div class="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-2xl p-8 border border-white/30 dark:border-slate-600/30 max-w-md mx-auto">
          <lucide-icon name="file" size="48" class="text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">{{ $t('BUSINESS_DATA.ITEMS.EMPTY_TITLE') }}</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">{{ $t('BUSINESS_DATA.ITEMS.EMPTY_DESCRIPTION') }}</p>
          <woot-button @click="$emit('add-data-item')" variant="smooth" class="input-base">
            {{ $t('BUSINESS_DATA.ITEMS.ADD_FIRST_ITEM') }}
          </woot-button>
        </div>
      </div>
      
      <!-- Data Items List -->
      <div v-else class="space-y-4">
        <div
          v-for="item in dataItems"
          :key="item.id"
          class="group bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-md p-2 border border-n-gray-1 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-800/80 hover:border-blue-200/50 dark:hover:border-blue-500/30 hover:shadow-lg transition-all duration-300"
        >
          <div class="flex items-start gap-2 sm:gap-3">
            <input
              type="checkbox"
              class="mt-1 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-slate-300 text-hichat-500 focus:ring-hichat-400"
              :checked="selectedIds.includes(item.id)"
              @change="onToggleSelect(item.id, $event.target.checked)"
            />
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <h4 class="text-base font-semibold text-slate-900 dark:text-slate-100 break-words" v-html="hl(item.title)"></h4>
                <span
                  v-if="item.web_content_source"
                  class="flex items-center gap-1 px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-xs font-medium"
                >
                  <fluent-icon icon="globe" size="12" />
                  {{ item.web_content_source.name }}
                </span>
                <span
                  class="px-2 py-0.5 rounded-lg text-xs font-medium"
                  :class="{
                    'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400': item.is_active,
                    'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400': !item.is_active
                  }"
                >
                  {{ item.is_active ? $t('BUSINESS_DATA.GENERAL.ACTIVE') : $t('BUSINESS_DATA.GENERAL.INACTIVE') }}
                </span>
              </div>

              <p class="text-sm leading-relaxed text-slate-600 dark:text-slate-300 whitespace-pre-line break-words" v-html="hl(item.description)"></p>

              <div v-if="item.web_content_source" class="mt-2 mb-2">
                <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <fluent-icon icon="link" size="12" />
                  <span class="font-mono break-all">{{ item.web_content_source.url }}</span>
                </div>
              </div>

              <div class="mt-3 flex flex-wrap items-center justify-between gap-2">
                <div class="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                  <span class="font-mono">ID: {{ item.id }}</span>
                  <span>{{ formatDate(item.created_at) }}</span>
                  <span
                    v-if="item.business_category"
                    class="px-2 py-1 rounded text-xs font-medium"
                    :style="{ backgroundColor: item.business_category.color + '20', color: item.business_category.color }"
                  >
                    {{ item.business_category.name }}
                  </span>
                </div>

                <div class="flex items-center gap-1 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                  <woot-button
                    variant="smooth"
                    size="small"
                    icon="edit"
                    color-scheme="secondary"
                    v-tooltip.top="$t('GENERAL.TOOLTIPS.EDIT')"
                    @click="$emit('edit-data-item', item)"
                  />
                  <woot-button
                    variant="smooth"
                    size="small"
                    :icon="item.is_active ? 'eye-hide' : 'eye-show'"
                    color-scheme="secondary"
                    v-tooltip.top="item.is_active ? $t('GENERAL.TOOLTIPS.HIDE') : $t('GENERAL.TOOLTIPS.SHOW')"
                    @click="$emit('toggle-status', item)"
                  />
                  <woot-button
                    variant="smooth"
                    size="small"
                    icon="dismiss-circle"
                    color-scheme="alert"
                    v-tooltip.top="$t('GENERAL.TOOLTIPS.DELETE')"
                    @click="$emit('delete-data-item', item)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Pagination -->
    <TableFooter
      v-if="!isLoading && dataItems.length > 0 && pagination.total_pages > 1"
      :current-page="pagination.current_page"
      :total-count="pagination.total_count"
      :page-size="pagination.per_page"
      class="border-slate-50 dark:border-slate-800 border-t !px-0 py-4"
      @page-change="goToPage"
    />
  </div>

  <ImportResultsModal
    :show="showImportResultsModal"
    :results="importResults || { created: 0, updated: 0, failed_count: 0, failed: [] }"
    @close="closeImportResults"
  />
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import { highlightText as highlightTextUtil } from 'shared/utils/searchHighlight';
import { formatDateTimeLocalized } from 'shared/helpers/DateHelper';
import BulkActionsBar from './BulkActionsBar.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import ImportResultsModal from '../ImportResultsModal.vue';

export default {
  name: 'DataItemsTab',
  components: { 
    Spinner, 
    BulkActionsBar,
    TableFooter,
    ImportResultsModal,
  },
  props: {
    dataItems: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    webSources: {
      type: Array,
      default: () => [],
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
      }),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isSearching: {
      type: Boolean,
      default: false,
    },
    isExporting: {
      type: Boolean,
      default: false,
    },
    isImporting: {
      type: Boolean,
      default: false,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    isBulkDeleting: {
      type: Boolean,
      default: false,
    },
    bulkAction: {
      type: String,
      default: '',
    },
    importResults: {
      type: Object,
      default: null,
    },
  },
  emits: [
    'add-data-item',
    'edit-data-item',
    'search',
    'filter-change',
    'page-change',
    'toggle-select',
    'toggle-select-page',
    'clear-selection',
    'bulk-update-status',
    'bulk-delete',
    'toggle-status',
    'delete-data-item',
    'export',
    'import',
  ],
  data() {
    return {
      searchQuery: '',
      selectedCategory: '',
      selectedStatus: '',
      selectedSource: '',
      selectedSort: 'created_at_desc',
      searchTimeout: null,
      showImportResultsModal: false,
    }
  },
  computed: {
    normalizedCategories() {
      const map = new Map()
      ;(this.categories || []).forEach(category => {
        const normalized = this.normalizeCategory(category)
        if (normalized && normalized.value && !map.has(normalized.value)) {
          map.set(normalized.value, normalized.label)
        }
      })
      return Array.from(map.entries()).map(([value, label]) => ({ value: String(value), label }))
    },
    hasBulkSelection() {
      return this.selectedIds.length > 0
    },
    selectedCount() {
      return this.selectedIds.length
    },
    isAllSelectedOnPage() {
      if (!this.dataItems.length) return false
      return this.dataItems.every(item => this.selectedIds.includes(item.id))
    },
  },
  watch: {
    categories: {
      immediate: true,
      handler() {
        if (!this.selectedCategory) return
        const exists = this.normalizedCategories.some(category => category.value === this.selectedCategory)
        if (!exists) {
          this.selectedCategory = ''
          this.onFilterChange()
        }
      },
    },
  },
  methods: {
    showImportResults() {
      this.showImportResultsModal = true
    },
    closeImportResults() {
      this.showImportResultsModal = false
    },
    normalizeCategory(category) {
      if (category === null || category === undefined || category === '') {
        return {
          value: 'uncategorized',
          label: this.$t('BUSINESS_DATA.ITEMS.UNCATEGORIZED'),
        }
      }
      if (typeof category === 'string') {
        const trimmed = category.trim()
        if (!trimmed) {
          return {
            value: 'uncategorized',
            label: this.$t('BUSINESS_DATA.ITEMS.UNCATEGORIZED'),
          }
        }
        return { value: trimmed, label: trimmed }
      }

      const value = category.id ?? category.slug ?? category.code ?? category.name ?? ''
      const label = category.name ?? category.display_name ?? category.label ?? category.slug ?? category.code

      if (!value && !label) {
        return {
          value: 'uncategorized',
          label: this.$t('BUSINESS_DATA.ITEMS.UNCATEGORIZED'),
        }
      }

      return {
        value: String(label || value),
        label: label || this.$t('BUSINESS_DATA.ITEMS.UNCATEGORIZED'),
      }
    },
    triggerImport() {
      this.$refs.fileInput?.click()
    },
    onFileSelected(event) {
      const file = event.target.files?.[0]
      if (!file) return
      this.$emit('import', file)
      event.target.value = ''
    },
    onExport() {
      this.$emit('export')
    },
    onSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.$emit('search', this.searchQuery.trim())
      }, 500)
    },
    onFilterChange() {
      this.$emit('filter-change', {
        category: this.selectedCategory,
        status: this.selectedStatus,
        web_content_source_id: this.selectedSource === 'manual' ? null : this.selectedSource,
        sort: this.selectedSort,
      })
    },
    goToPage(page) {
      if (page >= 1 && page <= this.pagination.total_pages && page !== this.pagination.current_page) {
        this.$emit('page-change', page)
      }
    },
    emitBulkStatus(isActive) {
      this.$emit('bulk-update-status', isActive)
    },
    onToggleSelect(id, checked) {
      this.$emit('toggle-select', { id, checked })
    },
    toggleSelectOnPage() {
      const ids = this.dataItems.map(item => item.id)
      this.$emit('toggle-select-page', { ids, selectAll: !this.isAllSelectedOnPage })
    },
    hl(text) {
      return highlightTextUtil(text, this.searchQuery, 'search-highlight')
    },
    formatDate(dateString) {
      return formatDateTimeLocalized(dateString, {
        includeSeconds: false,
        localeOverride: this.$i18n?.locale,
      })
    },
  },
}
</script>

<style scoped>
.search-highlight {
  background-color: #fef08a;
  color: #92400e;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-weight: 600;
}

.dark .search-highlight {
  background-color: #fbbf24;
  color: #451a03;
}
</style>
